/**
 * L'écran de préparation des tournées — étape 4 du chantier.
 *
 * Ce fichier ne calcule RIEN. Tout le calcul est dans `tournee.ts` et
 * `isometrie.ts`, éprouvé sans navigateur par 91 tests, et le comportement
 * vient de `docs/maquettes/tournees-essai.html`, où il a été essayé cinq fois
 * avant d'être codé. Ici, il n'y a que du rendu et des appels.
 *
 *
 * TROIS PANNEAUX, ET NON QUATRE — CE QUI MANQUE ET POURQUOI
 * ---------------------------------------------------------------------------
 * L'utilisateur avait dicté quatre quarts : les étapes en haut à gauche, LA
 * CARTE en haut à droite, les tournées en bas à gauche, le détail en bas à
 * droite.
 *
 * **La carte n'est pas là.** Elle demande trois choses qui n'existent pas
 * encore : `latitude` et `longitude` remises sur les SITES — elles ont été
 * supprimées de `clients` le 03/09 —, leur géocodage, et surtout **une source
 * de fond de carte, qui n'est pas tranchée**. Le dépôt ne dessine pas un
 * littoral de mémoire : ce serait faux.
 *
 * Plutôt qu'un quart vide, ou pire une porte qui ne mène nulle part — ce que
 * le menu du logiciel s'interdit déjà —, le détail prend toute la colonne de
 * droite. Le jour où la carte arrivera, il redescendra dans son quart.
 *
 *
 * ON COCHE, ON CHOISIT, ON AFFECTE
 * ---------------------------------------------------------------------------
 * Le glisser-déposer a été écarté dès la 2e version de l'essai, jugé
 * « compliqué à utiliser » : il ne porte qu'une ligne à la fois et il demande
 * de viser. On coche des étapes, on choisit une tournée, on clique
 * « Affecter ». Le geste se voit, il porte sur plusieurs lignes d'un coup.
 *
 *
 * LA DÉCOUPE EST CELLE DES AUTRES ÉCRANS : `VuePreparation` dessine et ne
 * décide rien ; `EcranTournees` tient l'état, appelle l'API et lui passe tout
 * en propriétés. C'est ce qui permet de REGARDER l'écran hors navigation, avec
 * un jeu d'essai, plutôt que de le déclarer bon sans l'avoir vu.
 *
 *
 * ÉCRITURE À CHAQUE GESTE, PAS DE BROUILLON
 * ---------------------------------------------------------------------------
 * Comme le planning. Rien à perdre si le navigateur ferme, et deux personnes
 * voient la même chose. Chaque geste relit la fenêtre entière : c'est un appel
 * de plus, et c'est ce qui garantit qu'on ne raisonne jamais sur un état que
 * l'écran aurait deviné.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  affecterALaTournee,
  attelerTournee,
  creerTournee,
  lireListeReglee,
  lireParc,
  lirePreparation,
  ordonnerLeChargement,
  reordonnerLaRoute,
  sortirDeLaTournee,
  supprimerTournee,
  type EchecApi,
  type LigneArret,
  type LigneReglee,
  type LigneTournee,
  type Preparation,
} from './api.js';
import { mlLisible, poser, refuse, type Support } from '@stafa/core';
import {
  cumuler,
  deplacerLeLot,
  jauger,
  lots,
  lotsDuBord,
  periode,
  ranger,
  recapituler,
  type Arret,
  type Lot,
  type PoseDuLot,
} from './tournee.js';
import { dessiner, type Caisse } from './isometrie.js';
import { PARC_CHAUFFEURS, PARC_REMORQUES, PARC_VEHICULES } from './parcs.js';
import { texte, type LigneParc } from './parc.js';

/** Ce que l'API peut refuser ici, dit en français. Jamais un code à l'écran. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cette tournée ou cette étape n’existe plus. Rechargez la fenêtre.',
  injoignable: 'Stafa ne répond pas. Réessayez dans un instant.',
  code_pris: 'Ce code est déjà pris.',
  invalide: 'Cette demande a été refusée : vérifiez ce qui a été saisi.',
  conflit: 'Cette ressource est déjà prise sur la période.',
  encore_actif: 'Encore actif : désactivez-le d’abord.',
  encore_rattache:
    'Cette tournée porte encore des arrêts : sortez-les avant de la supprimer.',
  ordre_annule: 'Cet ordre est annulé.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee: 'Cette étape est déjà validée.',
  jour_sans_etape: 'Aucune étape ce jour-là.',
  etape_en_tournee: 'Cette étape appartient déjà à une tournée.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
};

type Etat =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne'; readonly cause: EchecApi }
  | {
      readonly nom: 'pret';
      readonly preparation: Preparation;
      readonly supports: readonly LigneReglee[];
      readonly vehicules: readonly LigneParc[];
      readonly chauffeurs: readonly LigneParc[];
      readonly remorques: readonly LigneParc[];
    };

interface Proprietes {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}

// ---------------------------------------------------------------------------
// De quoi écrire des dates et des nombres
// ---------------------------------------------------------------------------

/** « 2026-09-04 » devient « 04/09 ». Découpe de chaîne, jamais de `Date`. */
function jourDit(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

/** Le premier jour de la fenêtre par défaut : aujourd'hui, en heure locale. */
function aujourdhui(): string {
  const d = new Date();
  const mois = String(d.getMonth() + 1).padStart(2, '0');
  const jour = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mois}-${jour}`;
}

/** Le jour décalé de n jours. Passe par UTC pour ignorer les heures d'été. */
function decaler(iso: string, jours: number): string {
  const t = Date.parse(`${iso}T00:00:00Z`) + jours * 86400000;
  return new Date(t).toISOString().slice(0, 10);
}

const euros = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/** Quatre teintes, comme dans l'essai. Franches : les douces ne se voient pas. */
const CLASSES_LOT = ['a', 'b', 'c', 'd'] as const;

/** La hauteur de caisse qu'on dessine faute de mieux. Ne sert QU'au dessin. */
const HAUTEUR_CAISSE_M = 2.7;

// ---------------------------------------------------------------------------
// Le pont entre les supports réglés et le calcul
// ---------------------------------------------------------------------------

/**
 * Comment une unité d'un lot se pose, d'après les dimensions du support.
 *
 * **Le dessin passe par la liste des supports ; le CUMUL passe par le
 * `ml_unitaire` figé sur l'étape.** Ce n'est pas une incohérence : le cumul est
 * un indicateur, qui ne doit pas bouger quand on corrige une fiche ; le dessin
 * est une image, qu'on redessine à chaque affichage de toute façon. Un support
 * renommé ou redimensionné change donc le dessin et non le chiffre — et c'est
 * le chiffre qui fait foi.
 */
function poseAvec(
  supports: readonly LigneReglee[],
): (lot: Lot) => PoseDuLot | null {
  const parNom = new Map(supports.map((s) => [s.nom.toLowerCase(), s]));
  return (lot: Lot) => {
    if (lot.support === null) return null;
    const trouve = parNom.get(lot.support.trim().toLowerCase());
    if (trouve === undefined) return null;

    const support: Support = {
      nom: trouve.nom,
      abrege: trouve.abrege ?? null,
      longueur_cm: trouve.longueur_cm ?? null,
      largeur_cm: trouve.largeur_cm ?? null,
      hauteur_cm: trouve.hauteur_cm ?? null,
      etages: trouve.etages ?? 1,
    };
    const r = poser(support);
    if (refuse(r)) return null;
    return {
      traversM: r.pose.traversCm / 100,
      profondeurM: r.pose.profondeurCm / 100,
      etages: r.pose.etages,
      hauteurM: support.hauteur_cm === null ? null : support.hauteur_cm / 100,
    };
  };
}

/** L'abrégé d'un support, ou son nom entier faute d'abrégé. */
function abregeDe(support: string | null, supports: readonly LigneReglee[]): string {
  if (support === null || support.trim() === '') return '—';
  const trouve = supports.find(
    (s) => s.nom.toLowerCase() === support.trim().toLowerCase(),
  );
  return trouve?.abrege ?? support;
}

// ---------------------------------------------------------------------------
// L'écran
// ---------------------------------------------------------------------------

export function EcranTournees({ jeton, quandAccesPerdu }: Proprietes) {
  const [etat, setEtat] = useState<Etat>({ nom: 'chargement' });
  const [du, setDu] = useState(aujourdhui());
  const [jours, setJours] = useState(3);
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState<'tout' | 'aplacer' | 'place'>('tout');
  const [cochees, setCochees] = useState<ReadonlySet<string>>(new Set());
  const [choisie, setChoisie] = useState<string | null>(null);
  const [onglet, setOnglet] = useState<'chargement' | 'ordres'>('chargement');
  /** L'arrêt où l'on s'est placé. `null` = au point le plus chargé. */
  const [moment, setMoment] = useState<number | null>(null);
  const [refusDit, setRefusDit] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const au = decaler(du, jours - 1);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  /**
   * Recharger la fenêtre entière.
   *
   * Appelée après CHAQUE geste. Un écran qui devine l'état après une écriture
   * finit par montrer autre chose que la base — et ici, ce qu'il montrerait,
   * c'est un camion qu'on croit chargé.
   */
  const charger = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;

    const [prep, sup, veh, cha, rem] = await Promise.all([
      lirePreparation(j, { du, au }),
      lireListeReglee(j, 'supports'),
      lireParc(j, PARC_VEHICULES),
      lireParc(j, PARC_CHAUFFEURS),
      lireParc(j, PARC_REMORQUES),
    ]);

    if (prep.etat !== 'ok') {
      setEtat({ nom: 'panne', cause: prep.cause });
      return;
    }
    setEtat({
      nom: 'pret',
      preparation: prep.valeur,
      supports: sup.etat === 'ok' ? sup.valeur : [],
      vehicules: veh.etat === 'ok' ? veh.valeur.filter((l) => l.actif) : [],
      chauffeurs: cha.etat === 'ok' ? cha.valeur.filter((l) => l.actif) : [],
      remorques: rem.etat === 'ok' ? rem.valeur.filter((l) => l.actif) : [],
    });
  }, [avecJeton, du, au]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Un geste : on écrit, on dit le refus s'il y en a un, on relit. */
  const geste = useCallback(
    async (faire: (j: string) => Promise<{ etat: 'ok' } | { etat: 'echec'; cause: EchecApi }>) => {
      const j = await avecJeton();
      if (j === null) return;
      setEnvoi(true);
      setRefusDit(null);
      const r = await faire(j);
      if (r.etat !== 'ok') {
        setRefusDit(REFUS[r.cause]);
        if (r.cause === 'acces') quandAccesPerdu();
      }
      await charger();
      setEnvoi(false);
    },
    [avecJeton, charger, quandAccesPerdu],
  );

  if (etat.nom === 'chargement') {
    return (
      <main className="corps corps-tournees">
        <p className="attente">Chargement de la préparation…</p>
      </main>
    );
  }
  if (etat.nom === 'panne') {
    return (
      <main className="corps corps-tournees">
        <p className="bandeau-refus">{REFUS[etat.cause]}</p>
      </main>
    );
  }

  return (
    <VuePreparation
      preparation={etat.preparation}
      supports={etat.supports}
      vehicules={etat.vehicules}
      chauffeurs={etat.chauffeurs}
      remorques={etat.remorques}
      du={du}
      au={au}
      jours={jours}
      recherche={recherche}
      filtre={filtre}
      cochees={cochees}
      choisie={choisie}
      onglet={onglet}
      moment={moment}
      refusDit={refusDit}
      envoi={envoi}
      quandDu={setDu}
      quandJours={setJours}
      quandRecherche={setRecherche}
      quandFiltre={setFiltre}
      quandCochees={setCochees}
      quandChoisie={(id) => {
        setChoisie(id);
        setMoment(null);
      }}
      quandOnglet={setOnglet}
      quandMoment={setMoment}
      quandGeste={geste}
    />
  );
}

/** Ce que la vue reçoit. Elle ne va rien chercher : tout lui est donné. */
export interface ProprietesVuePreparation {
  readonly preparation: Preparation;
  readonly supports: readonly LigneReglee[];
  readonly vehicules: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  readonly du: string;
  readonly au: string;
  readonly jours: number;
  readonly recherche: string;
  readonly filtre: 'tout' | 'aplacer' | 'place';
  readonly cochees: ReadonlySet<string>;
  readonly choisie: string | null;
  readonly onglet: 'chargement' | 'ordres';
  readonly moment: number | null;
  readonly refusDit: string | null;
  readonly envoi: boolean;
  readonly quandDu: (v: string) => void;
  readonly quandJours: (v: number) => void;
  readonly quandRecherche: (v: string) => void;
  readonly quandFiltre: (v: 'tout' | 'aplacer' | 'place') => void;
  readonly quandCochees: (v: ReadonlySet<string>) => void;
  readonly quandChoisie: (v: string | null) => void;
  readonly quandOnglet: (v: 'chargement' | 'ordres') => void;
  readonly quandMoment: (v: number | null) => void;
  readonly quandGeste: (
    faire: (j: string) => Promise<{ etat: 'ok' } | { etat: 'echec'; cause: EchecApi }>,
  ) => void;
}

export function VuePreparation({
  preparation,
  supports,
  vehicules,
  chauffeurs,
  remorques,
  du,
  au,
  jours,
  recherche,
  filtre,
  cochees,
  choisie,
  onglet,
  moment,
  refusDit,
  envoi,
  quandDu,
  quandJours,
  quandRecherche,
  quandFiltre,
  quandCochees,
  quandChoisie,
  quandOnglet,
  quandMoment,
  quandGeste,
}: ProprietesVuePreparation) {
  const tournee = preparation.tournees.find((t) => t.id === choisie) ?? null;

  return (
    <main className="corps corps-tournees">
      <BarreFenetre
        du={du}
        au={au}
        jours={jours}
        recherche={recherche}
        quandDu={quandDu}
        quandJours={quandJours}
        quandRecherche={quandRecherche}
      />

      {refusDit === null ? null : <p className="bandeau-refus">{refusDit}</p>}

      <div className="tournees-corps">
        <div className="tournees-gauche">
          <PanneauEtapes
            etapes={preparation.etapes}
            tournees={preparation.tournees}
            supports={supports}
            filtre={filtre}
            recherche={recherche}
            cochees={cochees}
            envoi={envoi}
            tournee={tournee}
            quandFiltre={quandFiltre}
            quandCocher={(id) => {
              const suite = new Set(cochees);
              if (suite.has(id)) suite.delete(id);
              else suite.add(id);
              quandCochees(suite);
            }}
            quandAffecter={() => {
              const cible = tournee;
              if (cible === null || cochees.size === 0) return;
              const liste = [...cochees];
              quandGeste(async (j) => {
                const r = await affecterALaTournee(j, cible.id, liste);
                if (r.etat === 'ok') quandCochees(new Set());
                return r;
              });
            }}
          />

          <PanneauTournees
            tournees={preparation.tournees}
            arrets={preparation.arrets}
            choisie={choisie}
            envoi={envoi}
            quandChoisir={quandChoisie}
            quandNouvelle={() =>
              quandGeste(async (j) => {
                const r = await creerTournee(j);
                if (r.etat === 'ok') quandChoisie(r.valeur.id);
                return r;
              })
            }
            quandSupprimer={() => {
              if (choisie === null) return;
              const id = choisie;
              quandGeste(async (j) => {
                const r = await supprimerTournee(j, id);
                if (r.etat === 'ok') quandChoisie(null);
                return r;
              });
            }}
          />
        </div>

        <div className="tournees-droite">
          {tournee === null ? (
            <p className="rien">
              Choisissez une tournée, ou créez-en une. Une tournée naît vide et sans
              camion : on y met des étapes, on les ordonne, et on l’attelle ensuite.
            </p>
          ) : (
            <PanneauDetail
              tournee={tournee}
              arrets={preparation.arrets.filter((a) => a.tournee_id === tournee.id)}
              supports={supports}
              vehicules={vehicules}
              chauffeurs={chauffeurs}
              remorques={remorques}
              tournees={preparation.tournees}
              tousLesArrets={preparation.arrets}
              onglet={onglet}
              moment={moment}
              envoi={envoi}
              quandOnglet={quandOnglet}
              quandMoment={quandMoment}
              quandAtteler={(attelage) =>
                quandGeste((j) => attelerTournee(j, tournee.id, attelage))
              }
              quandSortir={(etapeId) =>
                quandGeste((j) => sortirDeLaTournee(j, tournee.id, etapeId))
              }
              quandRoute={(ordre) =>
                quandGeste((j) => reordonnerLaRoute(j, tournee.id, ordre))
              }
              quandChargement={(ordre) =>
                quandGeste((j) => ordonnerLeChargement(j, tournee.id, ordre))
              }
            />
          )}
        </div>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// La fenêtre de travail
// ---------------------------------------------------------------------------

function BarreFenetre({
  du,
  au,
  jours,
  recherche,
  quandDu,
  quandJours,
  quandRecherche,
}: {
  readonly du: string;
  readonly au: string;
  readonly jours: number;
  readonly recherche: string;
  readonly quandDu: (v: string) => void;
  readonly quandJours: (v: number) => void;
  readonly quandRecherche: (v: string) => void;
}) {
  return (
    <div className="tournees-barre">
      <label htmlFor="tournees-du">Du</label>
      <input
        id="tournees-du"
        className="boite"
        type="date"
        value={du}
        onChange={(e) => quandDu(e.target.value)}
      />
      <span className="tournees-au">au {jourDit(au)}</span>
      {/* Les mêmes raccourcis que l'essai : on prépare rarement au-delà. */}
      {[1, 3, 7].map((n) => (
        <button
          key={n}
          type="button"
          className={`bouton bouton-discret${jours === n ? ' actif' : ''}`}
          onClick={() => quandJours(n)}
        >
          {n} jour{n > 1 ? 's' : ''}
        </button>
      ))}
      <input
        className="boite champ recherche"
        type="search"
        placeholder="Chercher : ordre, client, ville"
        value={recherche}
        onChange={(e) => quandRecherche(e.target.value)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Le panneau des étapes
// ---------------------------------------------------------------------------

function PanneauEtapes({
  etapes,
  tournees,
  supports,
  filtre,
  recherche,
  cochees,
  envoi,
  tournee,
  quandFiltre,
  quandCocher,
  quandAffecter,
}: {
  readonly etapes: readonly LigneArret[];
  readonly tournees: readonly LigneTournee[];
  readonly supports: readonly LigneReglee[];
  readonly filtre: 'tout' | 'aplacer' | 'place';
  readonly recherche: string;
  readonly cochees: ReadonlySet<string>;
  readonly envoi: boolean;
  readonly tournee: LigneTournee | null;
  readonly quandFiltre: (v: 'tout' | 'aplacer' | 'place') => void;
  readonly quandCocher: (id: string) => void;
  readonly quandAffecter: () => void;
}) {
  const cherche = recherche.trim().toLowerCase();
  const numeroDe = new Map(tournees.map((t) => [t.id, t.numero]));

  const visibles = etapes.filter((e) => {
    const placee = e.tournee_id !== null;
    if (filtre === 'aplacer' && placee) return false;
    if (filtre === 'place' && !placee) return false;
    if (cherche === '') return true;
    return [e.numero, e.client, e.site_ville, e.site_nom]
      .join(' ')
      .toLowerCase()
      .includes(cherche);
  });

  const aPlacer = etapes.filter((e) => e.tournee_id === null).length;
  const placees = etapes.length - aPlacer;

  return (
    <section className="tournees-panneau">
      <header className="tournees-entete">
        <h2>Étapes</h2>
        <span className="tournees-compte">
          {aPlacer} à placer · {placees} placée{placees > 1 ? 's' : ''}
        </span>
        <div className="tournees-bascule">
          {(
            [
              ['tout', 'Toutes'],
              ['aplacer', 'À placer'],
              ['place', 'Placées'],
            ] as const
          ).map(([cle, nom]) => (
            <button
              key={cle}
              type="button"
              className={`bouton bouton-discret${filtre === cle ? ' actif' : ''}`}
              onClick={() => quandFiltre(cle)}
            >
              {nom}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="bouton"
          disabled={envoi || tournee === null || cochees.size === 0}
          onClick={quandAffecter}
        >
          {tournee === null
            ? 'Affecter'
            : `Affecter ${cochees.size} à ${tournee.numero}`}
        </button>
      </header>

      {visibles.length === 0 ? (
        <p className="rien">Aucune étape ici.</p>
      ) : (
        <div className="tournees-defilant">
        <table className="tournees-liste">
          {/* Les parts sont POSÉES : en disposition automatique, la colonne du
              client tombait à 58 px dans un panneau de demi-écran. */}
          <colgroup>
            <col className="c-coche" />
            <col className="c-ordre" />
            <col />
            <col />
            <col className="c-charge" />
            <col className="c-etat" />
          </colgroup>
          <thead>
            <tr>
              <th />
              <th>Ordre</th>
              <th>Client</th>
              <th>Étape</th>
              <th className="n">Charge</th>
              <th>Tournée</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((e) => {
              const placee = e.tournee_id !== null;
              const ml =
                e.ml_unitaire === null || e.quantite === null
                  ? null
                  : Number(e.ml_unitaire) * Number(e.quantite);
              return (
                <tr
                  key={e.id}
                  className={`${placee ? 'placee' : 'aplacer'}${
                    cochees.has(e.id) ? ' cochee' : ''
                  }`}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={cochees.has(e.id)}
                      onChange={() => quandCocher(e.id)}
                      aria-label={`Cocher l’étape ${e.numero}`}
                    />
                  </td>
                  <td className="mono fort">{e.numero}</td>
                  <td className="coupe">{e.client ?? '—'}</td>
                  <td className="coupe">
                    <span className={`sens ${e.type}`}>
                      {e.type === 'chargement' ? '▼' : '▲'}
                    </span>{' '}
                    {e.site_ville ?? e.site_nom}
                    <span className="sous">
                      {jourDit(e.jour)}
                      {e.heure_debut === null ? '' : ` ${e.heure_debut}`}
                    </span>
                  </td>
                  <td className="n mono">
                    {e.quantite === null
                      ? '—'
                      : `${Number(e.quantite)} × ${abregeDe(e.support, supports)}`}
                    <span className="sous">
                      {ml === null ? 'ML inconnu' : `${mlLisible(ml)} ML`}
                    </span>
                  </td>
                  <td>
                    {placee ? (
                      /* LE NUMÉRO, ET NON « placée » : savoir QU'une étape est
                         prise ne sert à rien si l'on ne sait pas où elle est.
                         Il vient de la liste des tournées de la fenêtre ; une
                         tournée hors fenêtre n'y est pas, et on écrit alors
                         « placée » plutôt que d'inventer un numéro. */
                      <span className="etiq pla mono">
                        {numeroDe.get(e.tournee_id ?? '') ?? 'placée'}
                      </span>
                    ) : (
                      <span className="etiq att">à placer</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Le panneau des tournées
// ---------------------------------------------------------------------------

function PanneauTournees({
  tournees,
  arrets,
  choisie,
  envoi,
  quandChoisir,
  quandNouvelle,
  quandSupprimer,
}: {
  readonly tournees: readonly LigneTournee[];
  readonly arrets: readonly LigneArret[];
  readonly choisie: string | null;
  readonly envoi: boolean;
  readonly quandChoisir: (id: string) => void;
  readonly quandNouvelle: () => void;
  readonly quandSupprimer: () => void;
}) {
  return (
    <section className="tournees-panneau">
      <header className="tournees-entete">
        <h2>Tournées</h2>
        <span className="tournees-compte">
          {tournees.length} tournée{tournees.length > 1 ? 's' : ''}
        </span>
        <button type="button" className="bouton" disabled={envoi} onClick={quandNouvelle}>
          Nouvelle tournée
        </button>
        <button
          type="button"
          className="bouton bouton-discret danger"
          disabled={envoi || choisie === null}
          onClick={quandSupprimer}
        >
          Supprimer
        </button>
      </header>

      {tournees.length === 0 ? (
        <p className="rien">
          Aucune tournée sur cette fenêtre. « Nouvelle tournée » en ouvre une, vide.
        </p>
      ) : (
        <div className="tournees-defilant">
        <table className="tournees-liste">
          <colgroup>
            <col className="c-ordre" />
            <col className="c-periode" />
            <col />
            <col className="c-nombre" />
            <col className="c-ca" />
            <col className="c-jauge" />
          </colgroup>
          <thead>
            <tr>
              <th>Tournée</th>
              <th>Période</th>
              <th>Attelage</th>
              <th className="n">Arrêts</th>
              <th className="n">CA</th>
              <th>Remplissage</th>
            </tr>
          </thead>
          <tbody>
            {tournees.map((t) => {
              const siens = arrets.filter((a) => a.tournee_id === t.id);
              const r = cumuler(siens.map(enArret));
              const j = jauger(r, t.capacite_ml);
              const ca = recapituler(siens.map(enArret)).total;
              const p = periode(siens.map(enArret));
              return (
                <tr
                  key={t.id}
                  className={t.id === choisie ? 'choisie' : ''}
                  onClick={() => quandChoisir(t.id)}
                >
                  <td className="mono fort">{t.numero}</td>
                  <td className="mono">
                    {p === null
                      ? '—'
                      : p.plusieursJours
                        ? `${jourDit(p.debut)} → ${jourDit(p.fin)}`
                        : jourDit(p.debut)}
                  </td>
                  <td className="coupe">
                    {t.chauffeur_nom ?? (t.vehicule_id === null ? null : 'Sans chauffeur')}
                    {t.vehicule_id === null && t.remorque_id === null ? (
                      <span className="etiq libre">à atteler</span>
                    ) : (
                      <span className="sous">
                        {t.vehicule_immatriculation ?? '—'} +{' '}
                        {t.remorque_immatriculation ?? '—'}
                      </span>
                    )}
                  </td>
                  <td className="n mono">{t.arrets}</td>
                  <td className="n mono">{ca === 0 ? '—' : euros.format(ca)}</td>
                  <td>
                    <Jauge jauge={j} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}

/**
 * La jauge de remplissage.
 *
 * **Elle ne se plafonne pas.** Le dépassement se montre — barre rouge — il ne
 * se bloque pas : l'exploitation sait des choses que le logiciel ignore, et un
 * logiciel qui bloque se contourne en saisissant faux.
 *
 * Sans remorque, ou sans dimensions, elle écrit « ? » et ne dessine rien.
 * Inventer une capacité laisserait croire que ça rentre.
 */
function Jauge({ jauge }: { readonly jauge: ReturnType<typeof jauger> }) {
  if (jauge.part === null) {
    return <span className="tournees-jauge-muette">{jauge.texte}</span>;
  }
  const dedans = Math.min(1, jauge.part) * 100;
  const trop = Math.min(100 - dedans, Math.max(0, jauge.part - 1) * 100);
  return (
    <span className={`tournees-jauge${jauge.enTrop === 0 ? '' : ' deborde'}`}>
      <span className="barre">
        <i style={{ width: `${dedans.toFixed(1)}%` }} className="dedans" />
        {trop > 0 && <i style={{ width: `${trop.toFixed(1)}%` }} className="trop" />}
      </span>
      <span className="chiffre">{jauge.texte.replace(' ML', '')}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Le détail : les arrêts, le chargement, les ordres
// ---------------------------------------------------------------------------

/** Une ligne de l'API devient l'arrêt que le calcul attend. Rien de plus. */
function enArret(a: LigneArret): Arret {
  return {
    id: a.id,
    tournee_id: a.tournee_id,
    rang_tournee: a.rang_tournee,
    rang_chargement: a.rang_chargement,
    ot_id: a.ot_id,
    numero: a.numero,
    client: a.client,
    prix_vente: a.prix_vente,
    type: a.type,
    rang: a.rang,
    site_nom: a.site_nom,
    site_ville: a.site_ville,
    jour: a.jour,
    heure_debut: a.heure_debut,
    quantite: a.quantite,
    support: a.support,
    ml_unitaire: a.ml_unitaire,
  };
}

function PanneauDetail({
  tournee,
  arrets,
  supports,
  vehicules,
  chauffeurs,
  remorques,
  tournees,
  tousLesArrets,
  onglet,
  moment,
  envoi,
  quandOnglet,
  quandMoment,
  quandAtteler,
  quandSortir,
  quandRoute,
  quandChargement,
}: {
  readonly tournee: LigneTournee;
  readonly arrets: readonly LigneArret[];
  readonly supports: readonly LigneReglee[];
  readonly vehicules: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  readonly tournees: readonly LigneTournee[];
  readonly tousLesArrets: readonly LigneArret[];
  readonly onglet: 'chargement' | 'ordres';
  readonly moment: number | null;
  readonly envoi: boolean;
  readonly quandOnglet: (v: 'chargement' | 'ordres') => void;
  readonly quandMoment: (v: number | null) => void;
  readonly quandAtteler: (a: {
    vehicule_id: string | null;
    chauffeur_id: string | null;
    remorque_id: string | null;
  }) => void;
  readonly quandSortir: (etapeId: string) => void;
  readonly quandRoute: (ordre: readonly string[]) => void;
  readonly quandChargement: (ordre: readonly string[]) => void;
}) {
  const enCalcul = useMemo(() => arrets.map(enArret), [arrets]);
  const remplissage = useMemo(() => cumuler(enCalcul), [enCalcul]);
  const jauge = jauger(remplissage, tournee.capacite_ml);
  const p = periode(enCalcul);

  const route = remplissage.passages;
  const ordreActuel = route.map((x) => x.arret.id);

  return (
    <section className="tournees-panneau tournees-detail">
      <header className="tournees-entete">
        <h2>
          Tournée <span className="mono">{tournee.numero}</span>
        </h2>
        <span className="tournees-compte">
          {p === null
            ? 'vide'
            : p.plusieursJours
              ? `${jourDit(p.debut)} → ${jourDit(p.fin)}`
              : jourDit(p.debut)}{' '}
          · {arrets.length} arrêt{arrets.length > 1 ? 's' : ''}
        </span>
        <Attelage
          tournee={tournee}
          vehicules={vehicules}
          chauffeurs={chauffeurs}
          remorques={remorques}
          envoi={envoi}
          quandAtteler={quandAtteler}
        />
      </header>

      <div className="tournees-detail-corps">
        <div className="tournees-arrets">
          {route.length === 0 ? (
            <p className="rien">
              Aucun arrêt. Cochez des étapes à gauche, puis « Affecter ».
            </p>
          ) : (
            route.map((passage, i) => (
              <div
                key={passage.arret.id}
                className={`tournees-arret${moment === i ? ' ici' : ''}${
                  passage.livreeAvantChargee ? ' faute' : ''
                }`}
                onClick={() => quandMoment(moment === i ? null : i)}
              >
                <div className="fleches">
                  <button
                    type="button"
                    className="bouton-rond"
                    disabled={envoi || i === 0}
                    aria-label="Remonter cet arrêt"
                    onClick={(e) => {
                      e.stopPropagation();
                      quandRoute(echanger(ordreActuel, i, i - 1));
                    }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="bouton-rond"
                    disabled={envoi || i === route.length - 1}
                    aria-label="Descendre cet arrêt"
                    onClick={(e) => {
                      e.stopPropagation();
                      quandRoute(echanger(ordreActuel, i, i + 1));
                    }}
                  >
                    ↓
                  </button>
                </div>
                <span className={`sens ${passage.arret.type}`}>
                  {passage.arret.type === 'chargement' ? '▼' : '▲'}
                </span>
                <span className="ou">
                  {passage.arret.site_nom}
                  {passage.livreeAvantChargee && (
                    <span className="etiq refus">livrée avant son chargement</span>
                  )}
                </span>
                <span className="quand">
                  {jourDit(passage.arret.jour)}
                  {passage.arret.heure_debut === null
                    ? ''
                    : ` ${passage.arret.heure_debut}`}{' '}
                  · {passage.arret.site_ville ?? '—'}
                </span>
                <span className="quoi">
                  {passage.arret.quantite === null
                    ? '—'
                    : `${Number(passage.arret.quantite)}×${abregeDe(
                        passage.arret.support,
                        supports,
                      )}`}
                </span>
                <span className="reste">{mlLisible(passage.aBord)}</span>
                <span className="act">
                  <button
                    type="button"
                    className="bouton-rond"
                    disabled={envoi}
                    aria-label="Sortir cet arrêt de la tournée"
                    title="Sortir de la tournée — l’étape garde son camion"
                    onClick={(e) => {
                      e.stopPropagation();
                      quandSortir(passage.arret.id);
                    }}
                  >
                    ✕
                  </button>
                </span>
              </div>
            ))
          )}

        </div>

        <div className="tournees-vue">
          <div className="tournees-onglets">
            <button
              type="button"
              className={`bouton bouton-discret${onglet === 'chargement' ? ' actif' : ''}`}
              onClick={() => quandOnglet('chargement')}
            >
              Chargement
            </button>
            <button
              type="button"
              className={`bouton bouton-discret${onglet === 'ordres' ? ' actif' : ''}`}
              onClick={() => quandOnglet('ordres')}
            >
              Ordres
            </button>
            <span className="tournees-moment">
              {moment === null ? (
                'au plus chargé'
              ) : (
                <>
                  {moment + 1}
                  <sup>e</sup> arrêt{' '}
                  <button
                    type="button"
                    className="bouton bouton-discret"
                    onClick={() => quandMoment(null)}
                  >
                    ↺ le plus chargé
                  </button>
                </>
              )}
            </span>
          </div>

          {onglet === 'chargement' ? (
            <VueChargement
              enCalcul={enCalcul}
              remplissage={remplissage}
              jauge={jauge}
              moment={moment}
              supports={supports}
              remorques={remorques}
              tournee={tournee}
              envoi={envoi}
              quandChargement={quandChargement}
            />
          ) : (
            <VueOrdres
              enCalcul={enCalcul}
              tournee={tournee}
              tournees={tournees}
              tousLesArrets={tousLesArrets}
            />
          )}
        </div>
      </div>
    </section>
  );
}

/** Deux arrêts échangés dans la liste des identifiants. */
function echanger(ordre: readonly string[], i: number, j: number): readonly string[] {
  const suite = [...ordre];
  const a = suite[i];
  const b = suite[j];
  if (a === undefined || b === undefined) return ordre;
  suite[i] = b;
  suite[j] = a;
  return suite;
}

// ---------------------------------------------------------------------------
// L'attelage
// ---------------------------------------------------------------------------

/**
 * Le tracteur et la remorque se choisissent SÉPARÉMENT.
 *
 * C'est exactement l'échange de remorque : la remorque reste, la tête change.
 * Le chauffeur suit le tracteur par défaut — c'est l'API qui le recopie de
 * l'attelage du jour quand on ne dit rien.
 */
function Attelage({
  tournee,
  vehicules,
  chauffeurs,
  remorques,
  envoi,
  quandAtteler,
}: {
  readonly tournee: LigneTournee;
  readonly vehicules: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  readonly envoi: boolean;
  readonly quandAtteler: (a: {
    vehicule_id: string | null;
    chauffeur_id: string | null;
    remorque_id: string | null;
  }) => void;
}) {
  const poser = (
    cle: 'vehicule_id' | 'chauffeur_id' | 'remorque_id',
    valeur: string,
  ) => {
    // LES TROIS CLÉS SONT TOUJOURS ENVOYÉES : un corps qui en perdrait une
    // dételerait sans qu'on l'ait demandé.
    quandAtteler({
      vehicule_id: tournee.vehicule_id,
      chauffeur_id: tournee.chauffeur_id,
      remorque_id: tournee.remorque_id,
      [cle]: valeur === '' ? null : valeur,
    });
  };

  return (
    <div className="tournees-attelage">
      <select
        className="boite"
        aria-label="Tracteur"
        disabled={envoi}
        value={tournee.vehicule_id ?? ''}
        onChange={(e) => poser('vehicule_id', e.target.value)}
      >
        <option value="">Sans tracteur</option>
        {vehicules.map((v) => (
          <option key={v.id} value={v.id}>
            {texte(v, 'immatriculation') ?? '—'}
          </option>
        ))}
      </select>
      <select
        className="boite"
        aria-label="Chauffeur"
        disabled={envoi}
        value={tournee.chauffeur_id ?? ''}
        onChange={(e) => poser('chauffeur_id', e.target.value)}
      >
        <option value="">Sans chauffeur</option>
        {chauffeurs.map((c) => (
          <option key={c.id} value={c.id}>
            {texte(c, 'nom') ?? '—'}
          </option>
        ))}
      </select>
      <select
        className="boite"
        aria-label="Remorque"
        disabled={envoi}
        value={tournee.remorque_id ?? ''}
        onChange={(e) => poser('remorque_id', e.target.value)}
      >
        <option value="">Sans remorque</option>
        {remorques.map((r) => {
          const ml = texte(r, 'capacite_ml');
          return (
            <option key={r.id} value={r.id}>
              {texte(r, 'immatriculation') ?? '—'}
              {ml === null ? '' : ` · ${mlLisible(Number(ml))} ML`}
            </option>
          );
        })}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'onglet Chargement
// ---------------------------------------------------------------------------

function VueChargement({
  enCalcul,
  remplissage,
  jauge,
  moment,
  supports,
  remorques,
  tournee,
  envoi,
  quandChargement,
}: {
  readonly enCalcul: readonly Arret[];
  readonly remplissage: ReturnType<typeof cumuler>;
  readonly jauge: ReturnType<typeof jauger>;
  readonly moment: number | null;
  readonly supports: readonly LigneReglee[];
  readonly remorques: readonly LigneParc[];
  readonly tournee: LigneTournee;
  readonly envoi: boolean;
  readonly quandChargement: (ordre: readonly string[]) => void;
}) {
  const passage = moment === null ? null : remplissage.passages[moment];
  const bord = passage?.bord ?? remplissage.bordAuMaximum;
  const combien = passage === undefined || passage === null ? remplissage.maximum : passage.aBord;

  const tousLesLots = useMemo(() => lots(enCalcul), [enCalcul]);
  const lesLots = lotsDuBord(tousLesLots, bord);

  const capacite = jauge.capacite;
  const rangement = ranger(lesLots, poseAvec(supports), capacite);

  // La hauteur de caisse ne sert QU'au dessin : on la prend sur la fiche de la
  // remorque quand elle y est, sinon 2,70 m. Elle n'est pas figée sur la
  // tournée, et ce n'est pas grave — une image se redessine, un indicateur non.
  const fiche = remorques.find((r) => r.id === tournee.remorque_id);
  const hauteur = Number(texte(fiche ?? ({} as LigneParc), 'hauteur_interieure'));
  const caisse: Caisse | null =
    capacite === null
      ? null
      : {
          longueurM: capacite,
          largeurM: 2.4,
          hauteurM: Number.isFinite(hauteur) && hauteur > 0 ? hauteur : HAUTEUR_CAISSE_M,
        };

  const d = dessiner(rangement.unites, caisse);
  const cote = { dessus: 'ft', avant: 'fa', droite: 'fd' } as const;

  const reste =
    capacite === null || combien === null ? null : Math.max(0, capacite - combien);

  return (
    <div className="tournees-chargement">
      <div className="scene3d">
        <svg className="chargement" preserveAspectRatio="xMidYMid meet" viewBox={d.viewBox}>
          {d.sol !== null && <polygon className="sol" points={d.sol} />}
          {d.plafond !== null && <polygon className="arete" points={d.plafond} />}
          {d.montants.map((m, i) => (
            <line
              key={i}
              className="arete"
              x1={m.x1}
              y1={m.y1}
              x2={m.x2}
              y2={m.y2}
            />
          ))}
          {d.portes !== null && <polygon className="portes" points={d.portes} />}
          {d.faces.map((f, i) => (
            <g
              key={i}
              className={f.enTrop ? 'p-trop' : `p-${CLASSES_LOT[f.lot % 4] ?? 'a'}`}
            >
              <polygon className={cote[f.cote]} points={f.points} />
            </g>
          ))}
        </svg>
      </div>

      <div className="tournees-dit">
        {combien === null ? (
          <p>
            <strong>? ML</strong> à bord
            <br />
            <span className="attention">
              Un support n’a pas de dimensions : on ne sait pas ce qu’il occupe.
            </span>
          </p>
        ) : capacite === null ? (
          <p>
            <strong>{mlLisible(combien)} ML</strong> à bord
            <br />
            <span className="attention">
              Aucune remorque : on ne sait pas si ça rentre.
            </span>
          </p>
        ) : jauge.enTrop !== null && jauge.enTrop > 0 ? (
          <p>
            <strong className="alerte">{mlLisible(jauge.enTrop)} ML de trop</strong> sur{' '}
            {mlLisible(capacite)}
          </p>
        ) : (
          <p>
            <strong>
              {mlLisible(combien)} / {mlLisible(capacite)} ML
            </strong>
            , il en reste {mlLisible(reste)}
          </p>
        )}

        {/* LE PLANCHER RÉELLEMENT OCCUPÉ, quand il diffère de la jauge — et il
            diffère souvent. La jauge compte par CONVENTION un tiers de rang par
            palette ; le rangement place ce qu'il y a. Deux palettes valent
            0,80 ML mais occupent 1,20 m : le troisième emplacement est perdu.
            On le montre plutôt que de laisser croire à une erreur de calcul. */}
        {combien !== null && Math.abs(rangement.occupeM - combien) > 0.05 && (
          <p className="relais">
            au plancher : {mlLisible(rangement.occupeM)} m de rangs —{' '}
            {rangement.occupeM > combien ? 'des rangs incomplets' : 'des supports intercalés'}
          </p>
        )}

        {moment === null && remplissage.auDepart > 0 && (
          <p className="relais">
            {mlLisible(remplissage.auDepart)} ML repris d’une autre tournée
          </p>
        )}
        {moment === null && remplissage.aLArrivee !== null && remplissage.aLArrivee > 0.005 && (
          <p className="relais">
            {mlLisible(remplissage.aLArrivee)} ML remis au relais
          </p>
        )}
        {rangement.ignores > 0 && (
          <p className="relais">
            {rangement.ignores} lot{rangement.ignores > 1 ? 's' : ''} non dessiné
            {rangement.ignores > 1 ? 's' : ''} : support sans dimensions.
          </p>
        )}

        {/* LES LOTS SE DÉPLACENT ICI, aux flèches — le geste qui sert déjà aux
            arrêts. Pas de glisser-déposer : écarté dès la 2e version de
            l'essai. Le premier de la liste est AU FOND. Le rangement automatique
            met au fond ce qui se livre en dernier ; ces flèches servent à ce que
            le logiciel ignore — le fragile, le hayon, l'ADR. */}
        <div className="tournees-cles">
          {lesLots.map((lot, i) => (
            <span key={lot.arret_id} className="lot">
              <i className={`pastille-lot ${CLASSES_LOT[i % 4] ?? 'a'}`} />
              {lot.numero} · {lot.nombre} × {mlLisible(lot.ml_unitaire)} ML
              <button
                type="button"
                className="bouton-rond"
                disabled={envoi || i === 0}
                title="Vers le fond"
                aria-label={`Ranger le lot ${lot.numero} vers le fond`}
                onClick={() =>
                  quandChargement(deplacerLeLot(lesLots, lot.arret_id, 'vers_le_fond'))
                }
              >
                ↑
              </button>
              <button
                type="button"
                className="bouton-rond"
                disabled={envoi || i === lesLots.length - 1}
                title="Vers les portes"
                aria-label={`Ranger le lot ${lot.numero} vers les portes`}
                onClick={() =>
                  quandChargement(deplacerLeLot(lesLots, lot.arret_id, 'vers_les_portes'))
                }
              >
                ↓
              </button>
            </span>
          ))}
          {reste !== null && reste > 0.005 && (
            <span>
              <i className="pastille-lot libre" />
              {mlLisible(reste)} ML libres
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'onglet Ordres
// ---------------------------------------------------------------------------

function VueOrdres({
  enCalcul,
  tournee,
  tournees,
  tousLesArrets,
}: {
  readonly enCalcul: readonly Arret[];
  readonly tournee: LigneTournee;
  readonly tournees: readonly LigneTournee[];
  readonly tousLesArrets: readonly LigneArret[];
}) {
  /**
   * Où le CHARGEMENT de chaque ordre se trouve, quand ce n'est pas ici.
   *
   * Construit des AUTRES tournées de la fenêtre. Il peut rester vide — une
   * tournée hors fenêtre n'y sera pas —, et la mention est alors omise plutôt
   * qu'inventée.
   */
  const ailleurs = useMemo(() => {
    const numeroDe = new Map(tournees.map((t) => [t.id, t.numero]));
    const m = new Map<string, string>();
    for (const a of tousLesArrets) {
      if (a.type !== 'chargement') continue;
      if (a.tournee_id === null || a.tournee_id === tournee.id) continue;
      const numero = numeroDe.get(a.tournee_id);
      if (numero !== undefined && !m.has(a.ot_id)) m.set(a.ot_id, numero);
    }
    return m;
  }, [tousLesArrets, tournees, tournee.id]);

  const recap = recapituler(enCalcul, ailleurs);

  return (
    <div className="tournees-recap">
      <table className="tournees-liste">
        <colgroup>
          <col className="c-ordre" />
          <col />
          <col className="c-ca" />
          <col className="c-etat" />
        </colgroup>
        <thead>
          <tr>
            <th>Ordre</th>
            <th>Client</th>
            <th className="n">CA</th>
            <th>Part</th>
          </tr>
        </thead>
        <tbody>
          {recap.lignes.map((l) => (
            <tr key={l.ot_id}>
              <td className="mono fort">{l.numero}</td>
              <td className="coupe">{l.client ?? '—'}</td>
              <td className="n mono">
                <span className={l.compte ? 'ca' : 'ca ailleurs'}>
                  {l.compte
                    ? l.prix === null
                      ? '—'
                      : euros.format(l.prix)
                    : l.compteSur === null
                      ? 'chargé ailleurs'
                      : `sur ${l.compteSur}`}
                </span>
              </td>
              <td>
                {l.compte ? (
                  <span className="etiq pla">chargé ici</span>
                ) : (
                  <span className="etiq relais">relais</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>
              {recap.lignes.length} ordre{recap.lignes.length > 1 ? 's' : ''}
              {recap.repris === 0 ? '' : `, dont ${recap.repris} repris`}
            </td>
            <td className="n mono">{euros.format(recap.total)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
      <p className="tournees-note">
        Le prix d’un ordre est compté sur la tournée qui fait son{' '}
        <strong>chargement</strong> : sans cela, un ordre en relais serait compté deux
        fois, et la somme des tournées dépasserait le chiffre d’affaires réel.
      </p>
    </div>
  );
}
